import React, { useState, useEffect, useRef } from "react";
import "./StudentManagementTable.css";
import NoteModal from "./NoteModal";

function StudentManagementTable(props) {
  // Extract props
  const { 
    students, 
    classid, 
    teacherid, 
    countryId, 
    onChangesMade,
    gradeLevel,
    teacherName,
    bulkAddedStudents = [],
    teachers = [],
  } = props;
  
  // Local state management
  const [originalStudents, setOriginalStudents] = useState([]);
  const [localStudents, setLocalStudents] = useState([]);
  const [addedStudents, setAddedStudents] = useState([]);
  const [editedStudents, setEditedStudents] = useState([]);
  const [deletedStudents, setDeletedStudents] = useState([]);
  
  // Only need nextTempId for manual student addition
  const nextTempIdRef = useRef(-1000); // Start at -1000 to avoid conflicts with parent IDs

  // Track bulk-imported/ignored IDs to avoid re-adding after deletion
  const importedBulkIdsRef = useRef(new Set());

  // UI state
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingStudentName, setEditingStudentName] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStudentName, setNewStudentName] = useState("");
  
  // Deletion reasoning state
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");
  const [customDeletionReason, setCustomDeletionReason] = useState("");
  
  // Refs
  const nameInputRef = useRef(null);
  const newStudentInputRef = useRef(null);

  // Initialize local state when students prop changes
  useEffect(() => {
    if (students) {
      setOriginalStudents([...students]);
      setLocalStudents([...students]);
      // Reset all change tracking
      setAddedStudents([]);
      setEditedStudents([]);
      setDeletedStudents([]);
      // Reset temp ID counter
      nextTempIdRef.current = -1000;
      // Reset imported bulk IDs when the whole class is replaced
      importedBulkIdsRef.current = new Set();
    }
  }, [students]);

  // FIX: Sync bulkAddedStudents once per ID and never re-add removed ones
  useEffect(() => {
    if (!bulkAddedStudents?.length) return;

    // Consider only those bulk students we have not processed before
    const unseen = bulkAddedStudents.filter(s => !importedBulkIdsRef.current.has(s.id));
    if (!unseen.length) return;

    setLocalStudents(prev => {
      const existingIds = new Set(prev.map(s => s.id));
      const toAdd = unseen.filter(s => !existingIds.has(s.id));
      // Mark as imported so they won't be re-added later
      toAdd.forEach(s => importedBulkIdsRef.current.add(s.id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });

    setAddedStudents(prev => {
      const existingIds = new Set(prev.map(s => s.id));
      const toAdd = unseen.filter(s => !existingIds.has(s.id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [bulkAddedStudents]);

  // Focus on input when editing student name
  useEffect(() => {
    if (editingStudentId != null && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [editingStudentId]);
  
  // Focus on new student input when showing form
  useEffect(() => {
    if (showAddForm && newStudentInputRef.current) {
      newStudentInputRef.current.focus();
    }
  }, [showAddForm]);

  // Notify parent of changes whenever changes occur
  useEffect(() => {
    if (onChangesMade) {
      onChangesMade({
        added: addedStudents,
        edited: editedStudents,
        deleted: deletedStudents
      });
    }
  }, [addedStudents, editedStudents, deletedStudents, onChangesMade]);

  // Helper function to find student in local state
  const findLocalStudent = (studentId) => {
    return localStudents.find(s => s.id === studentId);
  };

  // Helper function to check if student was originally in the class
  const isOriginalStudent = (studentId) => {
    return originalStudents.some(s => s.id === studentId);
  };

  // Helper function to check if student is new (added manually or in bulk)
  const isNewStudent = (student) => !!student && !isOriginalStudent(student.id);

  // Helper function to check if student is deleted
  const isStudentDeleted = (studentId) => {
    return deletedStudents.some(s => s.id === studentId);
  };

  // Helper function to update edited students tracking
  const updateEditedStudentsTracking = (studentId, field, newValue, oldValue) => {
    if (!isOriginalStudent(studentId)) return; // Don't track edits for new students

    setEditedStudents(prev => {
      const existingEditIndex = prev.findIndex(s => s.id === studentId);
      
      if (existingEditIndex >= 0) {
        // Student already in edited list, update their fields
        const updatedEditedStudents = [...prev];
        const editedStudent = updatedEditedStudents[existingEditIndex];
        
        if (!editedStudent.fields) editedStudent.fields = [];
        if (!editedStudent.changes) editedStudent.changes = {};
        
        // Add field to list if not already there
        if (!editedStudent.fields.includes(field)) {
          editedStudent.fields.push(field);
        }
        
        // Store the new value
        editedStudent.changes[field] = newValue;
        
        return updatedEditedStudents;
      } else {
        // First edit for this student
        const originalStudent = originalStudents.find(s => s.id === studentId);
        return [...prev, {
          id: studentId,
          name: originalStudent?.name,
          fields: [field],
          changes: { [field]: newValue }
        }];
      }
    });
  };

  // Handle Select All for a specific field
  const handleSelectAll = (field, checked) => {
    // Get all non-deleted students
    const activeStudents = localStudents.filter(student => !isStudentDeleted(student.id));
    
    // Update each student using the existing handleCheckboxChange logic
    activeStudents.forEach(student => {
      handleCheckboxChange(student.id, field, checked);
    });
  };

  // Handle individual checkbox changes
  const handleCheckboxChange = (studentId, field, value) => {
    // Don't allow changes for deleted students
    if (isStudentDeleted(studentId)) return;
    
    // Update local student state
    setLocalStudents(prev => prev.map(student => 
      student.id === studentId 
        ? { ...student, [field]: value }
        : student
    ));

    // Track the change if it's an original student
    if (isOriginalStudent(studentId)) {
      const originalStudent = originalStudents.find(s => s.id === studentId);
      updateEditedStudentsTracking(studentId, field, value, originalStudent?.[field]);
    } else {
      // Update the added student
      setAddedStudents(prev => prev.map(student =>
        student.id === studentId
          ? { ...student, [field]: value }
          : student
      ));
    }
  };

  // Student name editing handlers
  const handleStudentNameDoubleClick = (student) => {
    // Don't allow editing deleted students
    if (isStudentDeleted(student.id)) return;
    
    setEditingStudentId(student.id);
    setEditingStudentName(student.name);
  };

  const handleNameInputChange = (e) => {
    setEditingStudentName(e.target.value);
  };

  const handleNameInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveStudentName();
    } else if (e.key === 'Escape') {
      setEditingStudentId(null);
    }
  };

  const handleNameInputBlur = () => {
    saveStudentName();
  };

  const saveStudentName = () => {
    if (editingStudentId != null && editingStudentName.trim()) {
      const oldName = findLocalStudent(editingStudentId)?.name;
      
      // Update local students
      setLocalStudents(prev => prev.map(student =>
        student.id === editingStudentId
          ? { ...student, name: editingStudentName.trim() }
          : student
      ));

      // Track the change if it's an original student
      if (isOriginalStudent(editingStudentId)) {
        updateEditedStudentsTracking(editingStudentId, 'name', editingStudentName.trim(), oldName);
      } else {
        // Update the added student
        setAddedStudents(prev => prev.map(student =>
          student.id === editingStudentId
            ? { ...student, name: editingStudentName.trim() }
            : student
        ));
      }
    }
    setEditingStudentId(null);
  };

  // Student deletion handlers
  const handleDeleteStudentClick = (studentId) => {
    const studentToDelete = findLocalStudent(studentId);
    if (!studentToDelete) return;

    // If it's a new student, delete immediately without requiring a reason
    if (isNewStudent(studentToDelete)) {
      // Prevent future re-import via bulk sync (safe for manual temp IDs too)
      importedBulkIdsRef.current.add(studentId);

      // Remove from local students
      setLocalStudents(prev => prev.filter(s => s.id !== studentId));
      
      // Remove from added students tracking
      setAddedStudents(prev => prev.filter(s => s.id !== studentId));
    } else {
      // For original students, show the deletion reason form
      setDeletingStudentId(studentId);
      setDeletionReason("Select a reason...");
      setCustomDeletionReason("");
    }
  };

  const handleCancelDeletion = () => {
    setDeletingStudentId(null);
    setDeletionReason("");
    setCustomDeletionReason("");
  };

  const handleConfirmDeletion = () => {
    const finalReason = deletionReason === "Other" 
      ? customDeletionReason.trim() 
      : deletionReason;

    if (!finalReason || finalReason === "Select a reason...") {
      alert("Please select or enter a reason for deletion.");
      return;
    }

    const studentToDelete = findLocalStudent(deletingStudentId);
    if (!studentToDelete) {
      handleCancelDeletion();
      return;
    }
    
    // Add to deleted list with reasoning
    setDeletedStudents(prev => [...prev, {
      ...studentToDelete,
      deletionReason: finalReason,
      deletedByTeacherId: teacherid,
      deletedByTeacherName: teacherName
    }]);

    // Reset deletion state
    handleCancelDeletion();
  };

  // Handle undeleting a student
  const handleUndeleteStudent = (studentId) => {
    // Remove from deleted students list
    setDeletedStudents(prev => prev.filter(s => s.id !== studentId));
  };

  // New student handlers
  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    if (!showAddForm) {
      setNewStudentName("");
    }
  };

  const handleAddStudent = (e) => {
    e.preventDefault();
    if (!newStudentName.trim()) return;

    // Create new student with temporary ID
    const newStudent = {
      id: nextTempIdRef.current,
      name: newStudentName.trim(),
      classid: classid,
      gradeLevel: gradeLevel,
      esl: false,
      sen: false,
      asd: false,
      gdd: false,
      send: false
    };

    // Add to local students
    setLocalStudents(prev => [...prev, newStudent]);
    
    // Add to added students tracking
    setAddedStudents(prev => [...prev, newStudent]);
    
    // Update temp ID for next student
    nextTempIdRef.current = nextTempIdRef.current - 1;
    
    // Reset form
    setNewStudentName("");
    setShowAddForm(false);
  };

  // Order students with new ones first, then alphabetically
  const orderedStudents = [...localStudents].sort((a, b) => {
    // New students (negative IDs or temp IDs) first
    const aIsNew = isNewStudent(a);
    const bIsNew = isNewStudent(b);
    
    if (aIsNew && !bIsNew) return -1;
    if (!aIsNew && bIsNew) return 1;
    
    // If both are new or both are original, sort alphabetically
    let fa = a.name.trim().toLowerCase(),
      fb = b.name.trim().toLowerCase();
    if (fa < fb) return -1;
    if (fa > fb) return 1;
    return 0;
  });

  // Country-specific IDs
  const irelandId = "07d45b79-24b1-435f-b82a-93861b23a86e";
  const englandId = "74bb2897-dbbe-41e3-b547-caee866545cf";

  // Simplified deletion reasons
  const deletionReasons = [
    "Select a reason...",
    "Repeating Student",
    "Student no longer in school",
    "Duplicate Entry",
    "Other"
  ];

  return (
    <div className="student-management-container">
      <div className="student-management-header">
        <h3 className="student-management-title">
          ({orderedStudents.length}) Students
        </h3>
        <button 
          onClick={toggleAddForm}
          className={`student-management-add-btn ${showAddForm ? 'cancel-mode' : 'add-mode'}`}
        >
          <span style={{ fontSize: '2vw' }}>
            {showAddForm ? '✕' : '+'}
          </span>
          {showAddForm ? "Cancel" : "Add New Student"}
        </button>
      </div>

      {orderedStudents.length === 0 && !showAddForm ? (
        <div className="student-management-empty-state">
          <div className="emoji">📚</div>
          <p className="title">
            No students in your class yet
          </p>
          <p className="subtitle">
            Add your first student to get started
          </p>
          <button 
            className="student-management-add-btn add-mode action-btn"
            onClick={toggleAddForm}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            Add Your First Student
          </button>
        </div>
      ) : (
        <>
          {showAddForm && (
            <div className="student-management-add-form">
              <form onSubmit={handleAddStudent} className="student-management-form-container">
                <input
                  ref={newStudentInputRef}
                  type="text"
                  className="student-management-name-input"
                  placeholder="Enter student name"
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  required
                />
                <button 
                  type="submit" 
                  className="student-management-action-btn success"
                >
                  Add Student
                </button>
              </form>
            </div>
          )}
          
          <div className="student-management-table-container">
            <table className="student-management-table">
              <thead>
                <tr>
                  <th className="student-management-th left-align">Name</th>
                  <th className="student-management-th">Notes</th>
                  <th className="student-management-th">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <span>EAL</span>
                      <input
                        type="checkbox"
                        className="student-management-checkbox"
                        onChange={(e) => handleSelectAll('esl', e.target.checked)}
                        title="Select all for EAL"
                        style={{ margin: 0 }}
                      />
                    </div>
                  </th>
                  <th className="student-management-th">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <span>Speech & Language</span>
                      <input
                        type="checkbox"
                        className="student-management-checkbox"
                        onChange={(e) => handleSelectAll('sen', e.target.checked)}
                        title="Select all for Speech & Language"
                        style={{ margin: 0 }}
                      />
                    </div>
                  </th>
                  {countryId === irelandId && (
                    <>
                      <th className="student-management-th">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span>ASD</span>
                          <input
                            type="checkbox"
                            className="student-management-checkbox"
                            onChange={(e) => handleSelectAll('asd', e.target.checked)}
                            title="Select all for ASD"
                            style={{ margin: 0 }}
                          />
                        </div>
                      </th>
                      <th className="student-management-th">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span>GDD</span>
                          <input
                            type="checkbox"
                            className="student-management-checkbox"
                            onChange={(e) => handleSelectAll('gdd', e.target.checked)}
                            title="Select all for GDD"
                            style={{ margin: 0 }}
                          />
                        </div>
                      </th>
                    </>
                  )}
                  {countryId === englandId && (
                    <>
                      <th className="student-management-th">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span>ASD</span>
                          <input
                            type="checkbox"
                            className="student-management-checkbox"
                            onChange={(e) => handleSelectAll('asd', e.target.checked)}
                            title="Select all for ASD"
                            style={{ margin: 0 }}
                          />
                        </div>
                      </th>
                      <th className="student-management-th">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span>GDD</span>
                          <input
                            type="checkbox"
                            className="student-management-checkbox"
                            onChange={(e) => handleSelectAll('gdd', e.target.checked)}
                            title="Select all for GDD"
                            style={{ margin: 0 }}
                          />
                        </div>
                      </th>
                      <th className="student-management-th">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <span>SEND</span>
                          <input
                            type="checkbox"
                            className="student-management-checkbox"
                            onChange={(e) => handleSelectAll('send', e.target.checked)}
                            title="Select all for SEND"
                            style={{ margin: 0 }}
                          />
                        </div>
                      </th>
                    </>
                  )}
                  <th className="student-management-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orderedStudents.map((student) => {
                  const isStudentNew = isNewStudent(student); // Use the helper function
                  const isDeleted = isStudentDeleted(student.id);
                  return (
                    <tr 
                      key={student.id} 
                      className={`student-management-row ${isStudentNew ? 'new-student' : ''} ${isDeleted ? 'deleted-student' : ''}`}
                    >
                      <td className="student-management-td name-cell">
                        {editingStudentId === student.id ? (
                          <input
                            ref={nameInputRef}
                            type="text"
                            className="student-management-name-input editing"
                            value={editingStudentName}
                            onChange={handleNameInputChange}
                            onBlur={handleNameInputBlur}
                            onKeyDown={handleNameInputKeyDown}
                          />
                        ) : (
                          <span
                            onDoubleClick={() => handleStudentNameDoubleClick(student)}
                            className={`student-management-editable-name ${isDeleted ? 'deleted-name' : ''}`}
                            title={isDeleted ? "This student has been marked as removed" : "Double-click to edit name"}
                          >
                            {student.name}
                            {isStudentNew && <span className="student-management-new-indicator"> (New)</span>}
                            {isDeleted && <span className="student-management-deleted-indicator"> (Removed)</span>}
                          </span>
                        )}
                      </td>
                      <td className="student-management-td">
                        {!isStudentNew && !isDeleted && (
                          <NoteModal
                            entityName={student.name}
                            elementtype="student"
                            elementid={student.id}
                            noteType="Admin"
                            teacherid={teacherid}
                            teachers={teachers}
                          />
                        )}
                      </td>
                      <td className="student-management-td">
                        <input
                          type="checkbox"
                          className="student-management-checkbox"
                          checked={student.esl || false}
                          onChange={(e) => handleCheckboxChange(student.id, "esl", e.target.checked)}
                          disabled={isDeleted}
                        />
                      </td>
                      <td className="student-management-td">
                        <input
                          type="checkbox"
                          className="student-management-checkbox"
                          checked={student.sen || false}
                          onChange={(e) => handleCheckboxChange(student.id, "sen", e.target.checked)}
                          disabled={isDeleted}
                        />
                      </td>
                      {countryId === irelandId && (
                        <>
                          <td className="student-management-td">
                            <input
                              type="checkbox"
                              className="student-management-checkbox"
                              checked={student.asd || false}
                              onChange={(e) => handleCheckboxChange(student.id, "asd", e.target.checked)}
                              disabled={isDeleted}
                            />
                          </td>
                          <td className="student-management-td">
                            <input
                              type="checkbox"
                              className="student-management-checkbox"
                              checked={student.gdd || false}
                              onChange={(e) => handleCheckboxChange(student.id, "gdd", e.target.checked)}
                              disabled={isDeleted}
                            />
                          </td>
                        </>
                      )}
                      {countryId === englandId && (
                        <>
                          <td className="student-management-td">
                            <input
                              type="checkbox"
                              className="student-management-checkbox"
                              checked={student.asd || false}
                              onChange={(e) => handleCheckboxChange(student.id, "asd", e.target.checked)}
                              disabled={isDeleted}
                            />
                          </td>
                          <td className="student-management-td">
                            <input
                              type="checkbox"
                              className="student-management-checkbox"
                              checked={student.gdd || false}
                              onChange={(e) => handleCheckboxChange(student.id, "gdd", e.target.checked)}
                              disabled={isDeleted}
                            />
                          </td>
                          <td className="student-management-td">
                            <input
                              type="checkbox"
                              className="student-management-checkbox"
                              checked={student.send || false}
                              onChange={(e) => handleCheckboxChange(student.id, "send", e.target.checked)}
                              disabled={isDeleted}
                            />
                          </td>
                        </>
                      )}
                      <td className="student-management-td">
                        {deletingStudentId === student.id ? (
                          <div className="student-management-delete-form">
                            <div className="delete-reason-container">
                              <select
                                value={deletionReason}
                                onChange={(e) => setDeletionReason(e.target.value)}
                                className="student-management-delete-reason-select"
                              >
                                {deletionReasons.map((reason, index) => (
                                  <option key={index} value={reason} disabled={index === 0}>
                                    {reason}
                                  </option>
                                ))}
                              </select>
                              
                              {deletionReason === "Other" && (
                                <input
                                  type="text"
                                  placeholder="Reason..."
                                  value={customDeletionReason}
                                  onChange={(e) => setCustomDeletionReason(e.target.value)}
                                  className="student-management-custom-reason-input-smaller"
                                  id="custom-deletion-reason"
                                  maxLength={200}
                                />
                              )}
                            </div>
                            
                            <div className="delete-action-buttons">
                              <button
                                onClick={handleConfirmDeletion}
                                className="student-management-action-btn delete-confirm"
                                title="Confirm deletion"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelDeletion}
                                className="student-management-action-btn delete-cancel"
                                title="Cancel deletion"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : isDeleted ? (
                          <button
                            onClick={() => handleUndeleteStudent(student.id)}
                            className="student-management-undelete-btn"
                          >
                            Reinstate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeleteStudentClick(student.id)}
                            className="student-management-delete-btn"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default StudentManagementTable;